import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, CheckCircle2, AlertCircle, ArrowLeft, X } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useToast } from '@/components/ui';
import {
  contactsApi,
  BulkImportField,
  BulkImportResult,
} from '@/lib/contactsApi';

/**
 * The full set of field-mapping options shown in the per-column
 * selectors. Order matters — most-used JP/EN fields first.
 */
const FIELD_OPTIONS: BulkImportField[] = [
  'ignore',
  'display_name',
  'family_name',
  'given_name',
  'family_name_kana',
  'given_name_kana',
  'email',
  'phone',
  'mobile',
  'title',
  'department',
  'company',
  'jp_corp_number',
  'country_code',
];

/**
 * CSV bulk-import page. Three states share the one route:
 *   1. Empty (no file picked) — show dropzone + intro.
 *   2. Preview (file uploaded once; server returned heuristic mapping +
 *      preview rows + ran one import). The user can re-map any column
 *      and re-import; the second import is idempotent against the first
 *      because account dedup is by (workspace_id, legal_name).
 *   3. Confirmed — show counts + errors + "view contacts" / "import
 *      another" CTAs.
 *
 * Toast policy matches the rest of the app: a single toast per finished
 * mutation, success or error.
 */
export function BulkImportPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewResult, setPreviewResult] = useState<BulkImportResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, BulkImportField>>({});
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const importMutation = useMutation({
    mutationFn: async (vars: {
      file: File;
      mapping?: Record<string, BulkImportField>;
    }) =>
      contactsApi.bulkImport({
        workspaceId: wsId,
        file: vars.file,
        mapping: vars.mapping,
      }),
    onSuccess: (data, vars) => {
      setPreviewResult(data);
      if (!vars.mapping) {
        // First (heuristic) pass — adopt the server's proposed mapping
        // as the editable base, but do *not* mark as confirmed: the
        // user still gets one chance to fix the columns.
        setMapping(data.previewMapping);
      } else {
        setHasConfirmed(true);
        toast.success(
          t('bulk_import.toast_imported', {
            count: data.imported,
          }),
        );
      }
    },
    onError: (err: unknown) => {
      const r = err as { response?: { data?: { message?: string } } };
      toast.error(r?.response?.data?.message || t('bulk_import.toast_failed'));
    },
  });

  function onPick(f: File | null) {
    if (!f) {
      setFile(null);
      setPreviewResult(null);
      setMapping({});
      setHasConfirmed(false);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setFile(f);
    setPreviewResult(null);
    setMapping({});
    setHasConfirmed(false);
    importMutation.mutate({ file: f });
  }

  function onConfirm() {
    if (!file) return;
    importMutation.mutate({ file, mapping });
  }

  return (
    <AppShell titleKey="bulk_import.title">
      <div className="mb-4">
        <Link
          to={`/app/workspace/${wsId}/people`}
          className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('bulk_import.back_to_people')}
        </Link>
      </div>

      <p className="text-sm text-ink-600 max-w-2xl mb-6">{t('bulk_import.intro')}</p>

      {/* ───── Dropzone ───── */}
      {!file && (
        <div
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            const f = e.dataTransfer.files?.[0];
            if (f) onPick(f);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          className={
            'rounded-md border-2 border-dashed p-10 text-center transition-colors ' +
            (dragActive
              ? 'border-accent-500 bg-accent-50'
              : 'border-ink-300 bg-white hover:border-accent-500')
          }
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv,application/csv,application/vnd.ms-excel,text/plain"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0] || null)}
          />
          <Upload className="mx-auto h-8 w-8 text-ink-400" />
          <p className="mt-3 text-sm text-ink-700 font-medium">
            {t('bulk_import.dropzone_label')}
          </p>
          <p className="mt-1 text-xs text-ink-500">{t('bulk_import.dropzone_hint')}</p>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-ink-300 text-sm font-semibold text-ink-900 hover:bg-ink-100"
          >
            <FileText className="h-4 w-4" />
            {t('bulk_import.choose_file')}
          </button>
        </div>
      )}

      {/* ───── Selected file banner ───── */}
      {file && (
        <div className="rounded-md border border-ink-200 bg-white px-5 py-4 mb-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="h-5 w-5 text-accent-700 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-ink-900 text-sm truncate">{file.name}</p>
              <p className="num text-xs text-ink-500">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onPick(null)}
            disabled={importMutation.isPending}
            aria-label={t('common.cancel')}
            className="text-ink-500 hover:text-ink-900 p-1 rounded-md hover:bg-ink-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {importMutation.isPending && !previewResult && (
        <p className="text-sm text-ink-500">{t('bulk_import.parsing')}</p>
      )}

      {/* ───── Mapping editor + preview (post-first-upload, pre-confirm) ───── */}
      {previewResult && !hasConfirmed && previewResult.preview.headers.length > 0 && (
        <>
          <section className="rounded-md border border-ink-200 bg-white mb-4">
            <header className="px-5 py-3 border-b border-ink-200 bg-ink-50">
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-ink-500 font-semibold">
                {t('bulk_import.mapping_title')}
              </p>
            </header>
            <p className="text-xs text-ink-500 px-5 pt-3">
              {t('bulk_import.mapping_hint')}
            </p>
            <div className="px-5 py-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {previewResult.preview.headers.map((h) => (
                <div
                  key={h}
                  className="flex items-center gap-3 text-sm border border-ink-100 rounded-md px-3 py-2"
                >
                  <span className="flex-1 truncate text-ink-700" title={h}>
                    {h}
                  </span>
                  <span className="text-ink-400" aria-hidden>
                    →
                  </span>
                  <select
                    value={mapping[h] ?? 'ignore'}
                    onChange={(e) =>
                      setMapping((m) => ({
                        ...m,
                        [h]: e.target.value as BulkImportField,
                      }))
                    }
                    className="rounded-md border border-ink-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                  >
                    {FIELD_OPTIONS.map((f) => (
                      <option key={f} value={f}>
                        {t(`bulk_import.field_${f}`)}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-md border border-ink-200 bg-white mb-4">
            <header className="px-5 py-3 border-b border-ink-200 bg-ink-50">
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-ink-500 font-semibold">
                {t('bulk_import.preview_title')}
              </p>
            </header>
            <div className="overflow-x-auto px-5 py-3">
              <table className="min-w-full text-xs">
                <thead className="text-left text-ink-500 border-b border-ink-200">
                  <tr>
                    {previewResult.preview.headers.map((h) => (
                      <th
                        key={h}
                        className="py-2 px-2 font-medium whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewResult.preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-ink-100">
                      {previewResult.preview.headers.map((h) => (
                        <td
                          key={h}
                          className="py-1.5 px-2 text-ink-700 whitespace-nowrap max-w-xs truncate"
                        >
                          {r[h] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="flex items-center gap-2 mb-6">
            <button
              type="button"
              onClick={onConfirm}
              disabled={importMutation.isPending}
              className="text-sm px-4 py-2 rounded-md bg-ink-900 text-white font-semibold hover:bg-ink-800 disabled:opacity-60"
            >
              {importMutation.isPending
                ? t('bulk_import.importing')
                : t('bulk_import.import_button')}
            </button>
            <button
              type="button"
              onClick={() => onPick(null)}
              className="text-sm px-4 py-2 rounded-md text-ink-700 hover:bg-ink-100"
            >
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}

      {/* ───── Result panel ───── */}
      {hasConfirmed && previewResult && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Summary tiles */}
          <div className="lg:col-span-4 space-y-3">
            <Tile
              tint="emerald"
              icon={<CheckCircle2 className="h-5 w-5" />}
              label={t('bulk_import.result_imported')}
              value={previewResult.imported}
            />
            <Tile
              tint="ink"
              icon={<FileText className="h-5 w-5" />}
              label={t('bulk_import.result_skipped')}
              value={previewResult.skipped}
            />
            {previewResult.errors.length > 0 && (
              <Tile
                tint="warn"
                icon={<AlertCircle className="h-5 w-5" />}
                label={t('bulk_import.result_errors')}
                value={previewResult.errors.length}
              />
            )}
            <div className="space-y-2">
              <Link
                to={`/app/workspace/${wsId}/people`}
                className="block text-center text-sm px-4 py-2 rounded-md bg-accent-500 text-white font-medium hover:bg-accent-600"
              >
                {t('bulk_import.view_contacts')}
              </Link>
              <button
                type="button"
                onClick={() => onPick(null)}
                className="block w-full text-sm px-4 py-2 rounded-md border border-ink-300 hover:bg-ink-100"
              >
                {t('bulk_import.import_another')}
              </button>
            </div>
          </div>

          {/* Errors / mapping echo */}
          <div className="lg:col-span-8 space-y-4">
            {previewResult.errors.length > 0 && (
              <section className="rounded-md border border-warn-500/30 bg-warn-50/50">
                <header className="px-5 py-3 border-b border-warn-500/30">
                  <p className="text-[10.5px] uppercase tracking-[0.18em] text-warn-600 font-semibold">
                    {t('bulk_import.errors_title', {
                      count: previewResult.errors.length,
                    })}
                  </p>
                </header>
                <ul className="divide-y divide-warn-500/20 max-h-72 overflow-y-auto">
                  {previewResult.errors.slice(0, 50).map((e, i) => (
                    <li
                      key={i}
                      className="px-5 py-2 text-sm flex items-baseline gap-3"
                    >
                      <span className="num text-xs text-warn-600 shrink-0">
                        {t('bulk_import.error_row', { row: e.row })}
                      </span>
                      <span className="text-ink-700">{e.reason}</span>
                    </li>
                  ))}
                </ul>
                {previewResult.errors.length > 50 && (
                  <p className="px-5 py-2 text-xs text-ink-500">
                    {t('bulk_import.error_truncated', {
                      count: previewResult.errors.length - 50,
                    })}
                  </p>
                )}
              </section>
            )}

            <section className="rounded-md border border-ink-200 bg-white">
              <header className="px-5 py-3 border-b border-ink-200 bg-ink-50">
                <p className="text-[10.5px] uppercase tracking-[0.18em] text-ink-500 font-semibold">
                  {t('bulk_import.mapping_title')}
                </p>
              </header>
              <dl className="divide-y divide-ink-100">
                {Object.entries(previewResult.previewMapping).map(([col, field]) => (
                  <div
                    key={col}
                    className="px-5 py-2.5 flex items-center justify-between text-sm"
                  >
                    <dt className="text-ink-700">{col}</dt>
                    <dd>
                      {field && field !== 'ignore' ? (
                        <span className="text-accent-700 num">→ {field}</span>
                      ) : (
                        <span className="text-ink-400 text-xs">
                          {t('bulk_import.field_ignore')}
                        </span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Tile({
  tint,
  icon,
  label,
  value,
}: {
  tint: 'emerald' | 'ink' | 'warn';
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  const styles = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    ink: 'border-ink-200 bg-white text-ink-900',
    warn: 'border-warn-500/30 bg-warn-50 text-warn-600',
  }[tint];
  return (
    <div
      className={`rounded-md border ${styles} px-5 py-4 flex items-center gap-3`}
    >
      <span className="opacity-70">{icon}</span>
      <div>
        <p className="num text-2xl font-medium tabular-nums leading-none">{value}</p>
        <p className="text-[11px] uppercase tracking-[0.16em] opacity-70 mt-1">
          {label}
        </p>
      </div>
    </div>
  );
}
